import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Translatable } from '@hrlabs/shared-types';
import { getRow, listCollection, saveRow } from '../../lib/db';
import { nextOrder } from '../../lib/order';
import { uploadToStorage } from '../../lib/upload';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';
import { ImageUploadField } from '../../components/ImageUploadField';

const EMPTY: Translatable = { en: '', bg: '' };

export function TeamEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);

  const [firstName, setFirstName] = useState<Translatable>({ ...EMPTY });
  const [lastName, setLastName] = useState<Translatable>({ ...EMPTY });
  const [position, setPosition] = useState<Translatable>({ ...EMPTY });
  const [shortDescription, setShort] = useState<Translatable>({ ...EMPTY });
  const [longDescription, setLong] = useState<Translatable>({ ...EMPTY });
  const [linkedin, setLinkedin] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [order, setOrder] = useState('');
  const [photoPath, setPhotoPath] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  useEffect(() => {
    if (!id) return;
    getRow('teamMembers', id).then((r) => {
      if (r) {
        setFirstName((r.firstName as Translatable) ?? { ...EMPTY });
        setLastName((r.lastName as Translatable) ?? { ...EMPTY });
        setPosition((r.position as Translatable) ?? { ...EMPTY });
        setShort((r.shortDescription as Translatable) ?? { ...EMPTY });
        setLong((r.longDescription as Translatable) ?? { ...EMPTY });
        setLinkedin(String(r.linkedin ?? ''));
        setEmail(String(r.email ?? ''));
        setTelephone(String(r.telephone ?? ''));
        setOrder(typeof r.order === 'number' ? String(r.order) : '');
        setPhotoPath((r.photoPath as string | null) ?? null);
      }
      setLoading(false);
    });
  }, [id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const savedId = await saveRow('teamMembers', id, {
        firstName,
        lastName,
        position,
        shortDescription,
        longDescription,
        linkedin: linkedin || null,
        email: email || null,
        telephone: telephone || null,
        ...(order.trim() !== ''
          ? { order: Number(order) }
          : id
            ? {}
            : { order: nextOrder(await listCollection('teamMembers')) }),
      });
      if (photoFile) {
        const { path } = await uploadToStorage('team-photos', savedId, photoFile);
        await saveRow('teamMembers', savedId, { photoPath: path });
      }
      notify('success', 'Team member saved.');
      navigate(`/team/${savedId}`);
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 760 }}>
      <h3 className="fw-bold mb-3">{id ? 'Edit team member' : 'New team member'}</h3>
      <BilingualField label="First name" value={firstName} onChange={setFirstName} required rows={1} />
      <BilingualField label="Last name" value={lastName} onChange={setLastName} required rows={1} />
      <BilingualField label="Position" value={position} onChange={setPosition} required rows={1} />
      <div className="mb-3" style={{ maxWidth: 160 }}>
        <label className="form-label fw-semibold">Order</label>
        <input type="number" className="form-control" value={order} onChange={(e) => setOrder(e.target.value)} />
        <div className="form-text">Lower numbers first (home shows the first three).</div>
      </div>
      <BilingualField label="Short description (not shown on the site)" value={shortDescription} onChange={setShort} required />
      <BilingualField label="Long description" value={longDescription} onChange={setLong} required rich />
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <label className="form-label fw-semibold">LinkedIn</label>
          <input className="form-control" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-semibold">Email (not shown)</label>
          <input className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-semibold">Telephone (not shown)</label>
          <input className="form-control" value={telephone} onChange={(e) => setTelephone(e.target.value)} />
        </div>
      </div>
      <ImageUploadField label="Photo" currentPath={photoPath} onPick={setPhotoFile} />
      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
